import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { update, updateMany } from "../../../../../api/onboarding";
import { GeaoContext } from "../../../GeaoContextProvider";
import { ApiRequestStatus } from "../../../constants/ApiRequestStatus";
import { GeaoPaths } from "../../../constants/GeaoPaths";
import { FlowType } from "../../../constants/FlowType";
import { PageStatus } from "../../../constants/PageStatus";

/**
 * Custom React hook for handling form events in Review and Sign forms.
 * Supports both Principal and Producer flows with appropriate navigation paths and data updates.
 * Manages form submission, save and exit functionality, and API request states.
 * 
 * @param {Object} options - Configuration options for the hook
 * @param {Function} [options.getValues=() => ({})] - Function to retrieve current form values
 * @param {Function} [options.trigger=() => Promise.resolve(true)] - Function to trigger form validation
 * @param {string} options.flowType - The flow type ('Principal' or 'Producer')
 * @param {string} options.componentName - The component name for activity tracking
 * @param {string} [options.saveAndExitPath] - Path to navigate to after a successful Save and Exit
 * @param {boolean} [options.validateOnSaveAndExit=true] - Whether to validate the form before Save and Exit
 * @param {boolean} [options.documentsWereSigned=false] - Whether documents were signed in this session (from DocuSign return)
 * @returns {Object} Object containing form event handlers and loading state
 * @returns {Function} returns.onSubmit - Handler for form submission that navigates to ApplicationSubmitted page
 * @returns {Function} returns.onSaveAndExit - Handler for saving progress and returning to checklist
 * @returns {boolean} returns.saving - Boolean indicating if an API request is in progress
 */
export default function useFormEvents({
    getValues = () => ({}),
    trigger = () => Promise.resolve(true),
    flowType,
    componentName,
    saveAndExitPath = GeaoPaths.CreateYourProfileChecklist,
    validateOnSaveAndExit = true,
    documentsWereSigned = false
}) {
    const navigate = useNavigate();

    const {
        agentInterview,
        setAgentInterview,
        agencyInterview,
        setAgencyInterview
    } = useContext(GeaoContext);

    const [updateStatus, setUpdateStatus] = useState(ApiRequestStatus.NotSent);
    const [nextPath, setNextPath] = useState(GeaoPaths.CreateYourProfileChecklist);

    const saving = updateStatus === ApiRequestStatus.Pending;

    // Validate required parameters
    if (!flowType || !componentName) {
        throw new Error('flowType and componentName are required parameters for useFormEvents');
    }

    if (!Object.values(FlowType).includes(flowType)) {
        throw new Error(`Invalid flowType: ${flowType}. Must be one of: ${Object.values(FlowType).join(', ')}`);
    }

    /**
     * Get the next path based on flow type
     */
    const getNextPath = () => {
        switch (flowType) {
            case FlowType.Principal:
                return GeaoPaths.PrincipalApplicationSubmitted;
            case FlowType.Producer:
                return GeaoPaths.ProducerApplicationSubmitted;
            default:
                return GeaoPaths.CreateYourProfileChecklist;
        }
    };

    /**
     * Effect hook that handles navigation after successful API updates.
     * Navigates to the specified next path after successful update.
     */
    useEffect(() => {
        if (updateStatus === ApiRequestStatus.Success) {
            navigate(nextPath);
        }
    }, [updateStatus, nextPath, navigate]);

    /**
     * Handles form submission and proceeds to the application submitted page.
     * Sets the appropriate next path based on flow type and updates the interview(s).
     * 
     * @param {Object} formData - The form data to be submitted
     */
    const onSubmit = (formData) => {
        setNextPath(getNextPath());
        updateInterview(formData, false);
    };

    /**
     * Handles the save and exit action.
     * Saves current form values and navigates to the save and exit path.
     * When validateOnSaveAndExit is true, form validation must pass first.
     */
    const onSaveAndExit = () => {
        if (!validateOnSaveAndExit) {
            setNextPath(saveAndExitPath);
            updateInterview(getValues(), true);
            return;
        }

        trigger().then(validationPassed => {
            if (validationPassed) {
                setNextPath(saveAndExitPath);
                updateInterview(getValues(), true);
            }
        });
    };

    /**
     * Builds question answers with ConfidentialityNDA if provided.
     */
    const buildQuestionAnswers = (baseAnswers, formData) => ({
        ...baseAnswers,
        ...(formData.confidentialityNda != null
            ? { ConfidentialityNDA: { answer: formData.confidentialityNda.toString() } }
            : {})
    });

    /**
     * Creates activity request for save and exit operations.
     */
    const createActivityRequest = (isSaveAndExit) => isSaveAndExit ? {
        ActivityCategory: "GEAO_ONBOARDING",
        ActivitySubType: "SAVE_AND_EXIT",
        SourceApplication: "AGIWUI",
        AdditionalData: { ComponentName: componentName }
    } : null;

    /**
     * Builds the page status map for an interview, marking this page complete.
     */
    const buildPageStatus = (interview) => ({
        ...interview?.pageStatus,
        [componentName]: PageStatus.Completed
    });

    /**
     * Builds agency data with signed sub-agency addendum flag if applicable.
     */
    const buildAgencyDataWithAddendum = () => {
        if (agencyInterview?.data?.HasSignedSubAgencyAddendum === undefined) {
            return agencyInterview?.data;
        }
        return { ...agencyInterview?.data, HasSignedSubAgencyAddendum: "true" };
    };

    /**
     * Updates Producer flow interview (agent only).
     */
    const updateProducerInterview = (formData, isSaveAndExit, activityRequest) => {
        const agentQuestionAnswers = buildQuestionAnswers(agentInterview?.questionAnswers, formData);

        if (documentsWereSigned && !isSaveAndExit) {
            agentQuestionAnswers.HasSignedFCRA = { answer: "true" };
        }

        update({
            identityType: "Agent",
            interview: {
                ...agentInterview,
                questionAnswers: agentQuestionAnswers,
                pageStatus: buildPageStatus(agentInterview)
            },
            activityRequest
        },
            interview => { setAgentInterview(interview); setUpdateStatus(ApiRequestStatus.Success); },
            () => { },
            () => { setUpdateStatus(ApiRequestStatus.Error); }
        );
    };

    /**
     * Updates Principal flow interviews (agent and agency).
     */
    const updatePrincipalInterview = (formData, isSaveAndExit, activityRequest) => {
        const agentQuestionAnswers = buildQuestionAnswers(agentInterview?.questionAnswers, formData);
        const agencyQuestionAnswers = buildQuestionAnswers(agencyInterview?.questionAnswers, formData);
        let agencyData = agencyInterview?.data;

        if (documentsWereSigned && !isSaveAndExit) {
            agentQuestionAnswers.HasSignedFCRA = { answer: "true" };
            agencyQuestionAnswers.HasSignedACH = { answer: "true" };
            agencyQuestionAnswers.HasSignedAgencyAgreement = { answer: "true" };
            agencyQuestionAnswers.HasSignedW9 = { answer: "true" };
            agencyData = buildAgencyDataWithAddendum();
        }

        updateMany([
            {
                identityType: "Agent",
                interview: {
                    ...agentInterview,
                    questionAnswers: agentQuestionAnswers,
                    pageStatus: buildPageStatus(agentInterview)
                },
                activityRequest
            },
            {
                identityType: "Agency",
                interview: {
                    ...agencyInterview,
                    questionAnswers: agencyQuestionAnswers,
                    pageStatus: buildPageStatus(agencyInterview),
                    ...(agencyData !== agencyInterview?.data && { data: agencyData })
                }
            }
        ],
            interviews => {
                setAgentInterview(interviews.find(i => i.interviewId === agentInterview?.interviewId));
                setAgencyInterview(interviews.find(i => i.interviewId === agencyInterview?.interviewId));
                setUpdateStatus(ApiRequestStatus.Success);
            },
            () => { },
            () => { setUpdateStatus(ApiRequestStatus.Error); }
        );
    };

    /**
     * Updates the interview(s) with form data via API call.
     */
    const updateInterview = (formData, isSaveAndExit) => {
        if (updateStatus === ApiRequestStatus.Pending) return;

        const activityRequest = createActivityRequest(isSaveAndExit);
        setUpdateStatus(ApiRequestStatus.Pending);

        if (flowType === FlowType.Producer) {
            updateProducerInterview(formData, isSaveAndExit, activityRequest);
        } else if (flowType === FlowType.Principal) {
            updatePrincipalInterview(formData, isSaveAndExit, activityRequest);
        }
    };

    return {
        onSubmit,
        onSaveAndExit,
        saving
    };
}
